import { Flex, Text } from '@radix-ui/themes';
import type { LinkState } from '@mac-remote/protocol';

const COLOR: Record<LinkState, string> = {
  online: 'var(--jade-9)',
  stale: 'var(--amber-9)',
  offline: 'var(--red-9)',
};

function ago(ts: number | null): string {
  if (!ts) return '';
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 60) return `${s} с назад`;
  if (s < 3600) return `${Math.round(s / 60)} мин назад`;
  return `${Math.round(s / 3600)} ч назад`;
}

/**
 * The label is deliberately hedged. A live socket does not prove the Mac is awake —
 * macOS keeps TCP alive across sleep — so "stale" genuinely means "not answering",
 * and guessing which cause it is would be dishonest.
 */
export function LinkBadge({
  state,
  latencyMs,
  lastSeenAt,
  compact = false,
}: {
  state: LinkState;
  latencyMs: number | null;
  lastSeenAt: number | null;
  /** Phone-width: the dot carries the state and only the latency keeps its text.
   *  "мак спит или потерял сеть" is 160px of header that a coloured dot already
   *  says — the full sentence survives in the tooltip and on the Mac panel. */
  compact?: boolean;
}) {
  const full =
    state === 'online'
      ? latencyMs != null
        ? `${latencyMs} мс`
        : 'на связи'
      : state === 'stale'
        ? 'мак спит или потерял сеть'
        : 'мак офлайн';
  const suffix = state !== 'online' && lastSeenAt ? ` · ${ago(lastSeenAt)}` : '';
  const label = compact && state !== 'online' ? '' : full;

  return (
    <Flex align="center" gap="2" style={{ flex: '0 0 auto' }} title={`${full}${suffix}`}>
      <span className="dot" style={{ background: COLOR[state] }} />
      {label && (
        <Text size="1" style={{ color: 'var(--app-hint)', whiteSpace: 'nowrap' }}>
          {label}
          {compact ? '' : suffix}
        </Text>
      )}
    </Flex>
  );
}
